import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    CircularProgress,
    Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Home as HomeIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import {
    expedienteInmueblesJWT, getCombosJWT,
    crearInmuebleJWT, editarInmuebleJWT, eliminarInmuebleJWT,
    expedienteTitularesJWT, crearTitularJWT, editarTitularJWT, eliminarTitularJWT,
    siguienteDniFicticioJWT, buscarContrapartePorDniJWT,
} from "../../api/endpoints";
import { normalizeKeys } from "../../utils/normalizeKeys";
import usePermisosModulo from "../../hooks/usePermisosModulo";

const InmueblesTab = ({ idExpediente, tipo, modulo }) => {
    // ---------------- Permisos por módulo ----------------
    const { permDe, ready: permReady } = usePermisosModulo(modulo);
    const permInm = permDe("PESTAÑA INMUEBLES", "INMUEBLES");
    const perm = { ready: permReady, ...permInm };

    // Estados principales
    const [inmuebles, setInmuebles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [inmuebleActual, setInmuebleActual] = useState(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Catálogos
    const [departamentos, setDepartamentos] = useState([]);
    const [localidades, setLocalidades] = useState([]);
    const [caracteres, setCaracteres] = useState([]);
    const [tiposDocumento, setTiposDocumento] = useState([]);

    // Expansión de inmuebles para ver titulares
    const [expandedInmueble, setExpandedInmueble] = useState(null);
    const [titulares, setTitulares] = useState([]);
    const [loadingTitulares, setLoadingTitulares] = useState(false);

    // Dialog de confirmación eliminar
    const [eliminarDialog, setEliminarDialog] = useState({ open: false, tipo: '', nombre: '', onConfirm: null });

    // Dialog de titulares
    const [openDialogTitular, setOpenDialogTitular] = useState(false);
    const [modoEdicionTitular, setModoEdicionTitular] = useState(false);
    const [titularActual, setTitularActual] = useState(null);
    const [inmuebleSeleccionado, setInmuebleSeleccionado] = useState(null);

    // Form de inmueble
    const [formInmueble, setFormInmueble] = useState({
        nomenclatura: '',
        inscripcion: '',
        tomo: '',
        folio: '',
        ano: '',
        plano: '',
        dpto: 0,
        real: '',
        sup_titulo: '',
        sup_plano: '',
        ubicacion: '',
        cp: '',
        interes_fiscal: 'NO',
        inspeccion_fe: 'NO',
        demanda_monto: '',
        demanda_fecha: '',
        demanda_obs: '',
        ley: '',
        monto_depositado: '',
        obs_antecedentes: '',
        obs_cauces_agua: '',
        obs_servidumbres: '',
        obs_mejoras: '',
        obs_deslinde: '',
        obs_fijacion: '',
        obs_geo: '',
        obs_observaciones: ''
    });

    // Form de titular
    const [formTitular, setFormTitular] = useState({
        id_caracter: 0,
        id_tipo_documento: 'A',
        dni: '',
        razon_social: '',
        nombre: '',
        direccion: '',
        cp: 0
    });

    // ⭐ Autocomplete de contrapartes
    const [contrapartes, setContrapartes] = useState([]);
    const [loadingContrapartes, setLoadingContrapartes] = useState(false);
    const [contraparteSeleccionada, setContraparteSeleccionada] = useState(null);

    // ============================================
    // BÚSQUEDA DE CONTRAPARTES (AUTOCOMPLETE)
    // ============================================
    const fetchContrapartes = async (q) => {
        if ((q || "").length < 2) return;
        setLoadingContrapartes(true);
        try {
            const data = await getCombosJWT('contraparte', q);
            const items = normalizeKeys(data?.items || []);
            setContrapartes(items.map(i => ({
                id: i.id,
                nombre: i.label || i.razon_social || '',
                razon_social: i.razon_social || '',
                nombre_persona: i.nombre || '',
                direccion: i.direccion || '',
                cp: i.cp || 0,
                id_tipo_documento: i.id_tipo_documento || 'A'
            })));
        } catch (error) {
            console.error('Error al buscar contrapartes:', error);
            setContrapartes([]);
        } finally {
            setLoadingContrapartes(false);
        }
    };

    // Generar DNI ficticio cuando se selecciona tipo doc "F"
    const handleTipoDocumentoChange = async (value) => {
        setFormTitular(prev => ({ ...prev, id_tipo_documento: value }));
        if (String(value).toUpperCase() === 'F') {
            try {
                const resp = await siguienteDniFicticioJWT(idExpediente);
                if (resp?.ok && resp.dni) {
                    setFormTitular(prev => ({ ...prev, id_tipo_documento: value, dni: String(resp.dni) }));
                    setContraparteSeleccionada(null);
                }
            } catch (error) {
                console.error('Error al generar DNI ficticio:', error);
            }
        }
    };

    // Buscar contraparte por DNI al perder foco
    const handleDniBlur = async () => {
        const dni = formTitular.dni;
        if (!dni || isNaN(Number(dni))) return;
        try {
            const resp = await buscarContrapartePorDniJWT(idExpediente, dni);
            if (resp?.ok && resp.data) {
                setFormTitular(prev => ({
                    ...prev,
                    id_tipo_documento: resp.data.id_tipo_documento || prev.id_tipo_documento,
                    razon_social: resp.data.razon_social || '',
                    nombre: resp.data.nombre || '',
                    direccion: resp.data.direccion || '',
                    cp: resp.data.cp || 0
                }));
                setContraparteSeleccionada({ id: resp.data.id, nombre: `${resp.data.razon_social || ''} ${resp.data.nombre || ''}`.trim() });
            }
        } catch { /* contraparte no encontrada, usuario llena manual */ }
    };

    // ============================================
    // UTILIDADES
    // ============================================
    const convertirFechaParaInput = (fecha) => {
        if (!fecha) return '';

        // Si viene en formato /Date(...)/ del servidor
        if (typeof fecha === 'string' && fecha.startsWith('/Date(')) {
            const timestamp = parseInt(fecha.match(/\d+/)[0]);
            const date = new Date(timestamp);
            return date.toISOString().split('T')[0];
        }

        // Si ya viene en formato yyyy-MM-dd
        if (typeof fecha === 'string' && fecha.includes('-')) {
            return fecha.split('T')[0];
        }

        return '';
    };

    // ============================================
    // CARGA INICIAL
    // ============================================
    useEffect(() => {
        cargarInmuebles();
        cargarCatalogos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idExpediente]);

    const cargarInmuebles = async () => {
        setLoading(true);
        try {
            const data = await expedienteInmueblesJWT(idExpediente);
            if (data.ok) {
                setInmuebles(normalizeKeys(data.inmuebles || []));
            }
        } catch (error) {
            console.error('Error al cargar inmuebles:', error);
            setSnackbar({ open: true, message: 'Error al cargar inmuebles', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const cargarCatalogos = async () => {
        try {
            const [carsData, dptosData, tiposDocData] = await Promise.all([
                getCombosJWT('caracteres'),
                getCombosJWT('codigos_postales'),
                getCombosJWT('tipos_documento'),
            ]);

            // Departamentos (Codigos_Postales)
            const dptosItems = normalizeKeys(dptosData?.items || []);
            setDepartamentos(dptosItems.map(d => ({ id: d.id, nombre: d.label })));
            setLocalidades(dptosItems.map(d => ({ id: d.id, nombre: d.label })));

            // Caracteres
            const carsItems = normalizeKeys(carsData?.items || []);
            setCaracteres(carsItems.map(c => ({ id: c.id, nombre: c.label || c.caracter })));

            // Tipos documento
            const tdItems = normalizeKeys(tiposDocData?.items || []);
            setTiposDocumento(tdItems.map(t => ({ id: t.id, nombre: t.label })));
        } catch (error) {
            console.error('Error al cargar catálogos:', error);
        }
    };

    // ============================================
    // MANEJO DE INMUEBLES
    // ============================================
    const handleAbrirDialogInmueble = (inmueble = null) => {
        if (inmueble) {
            setModoEdicion(true);
            setInmuebleActual(inmueble);
            cargarDetalleInmueble(inmueble.id);
        } else {
            setModoEdicion(false);
            setInmuebleActual(null);
            setFormInmueble({
                nomenclatura: '',
                inscripcion: '',
                tomo: '',
                folio: '',
                ano: '',
                plano: '',
                dpto: 0,
                real: '',
                sup_titulo: '',
                sup_plano: '',
                ubicacion: '',
                cp: '',
                interes_fiscal: 'NO',
                inspeccion_fe: 'NO',
                demanda_monto: '',
                demanda_fecha: '',
                demanda_obs: '',
                ley: '',
                monto_depositado: '',
                obs_antecedentes: '',
                obs_cauces_agua: '',
                obs_servidumbres: '',
                obs_mejoras: '',
                obs_deslinde: '',
                obs_fijacion: '',
                obs_geo: '',
                obs_observaciones: ''
            });
        }
        setTabValue(0);
        setOpenDialog(true);
    };

    const cargarDetalleInmueble = async (idInmueble) => {
        // Use data from the inmuebles list to populate the form
        const inm = inmuebles.find(i => i.id === idInmueble);
        if (inm) {
            setFormInmueble({
                nomenclatura: inm.nomenclatura || '',
                inscripcion: inm.inscripcion || '',
                tomo: inm.tomo || '',
                folio: inm.folio || '',
                ano: inm.ano || '',
                plano: inm.plano || '',
                dpto: inm.dpto || 0,
                real: inm.real || '',
                sup_titulo: inm.sup_titulo || '',
                sup_plano: inm.sup_plano || '',
                ubicacion: inm.ubicacion || '',
                cp: inm.cp || '',
                interes_fiscal: inm.interes_fiscal || 'NO',
                inspeccion_fe: inm.inspeccion_fe || 'NO',
                demanda_monto: inm.demanda_monto || '',
                demanda_fecha: convertirFechaParaInput(inm.demanda_fecha),
                demanda_obs: inm.demanda_obs || '',
                ley: inm.ley || '',
                monto_depositado: inm.monto_depositado || '',
                obs_antecedentes: inm.obs_antecedentes || '',
                obs_cauces_agua: inm.obs_cauces_agua || '',
                obs_servidumbres: inm.obs_servidumbres || '',
                obs_mejoras: inm.obs_mejoras || '',
                obs_deslinde: inm.obs_deslinde || '',
                obs_fijacion: inm.obs_fijacion || '',
                obs_geo: inm.obs_geo || '',
                obs_observaciones: inm.obs_observaciones || '',
            });
        }
    };

    const handleGuardarInmueble = async () => {
        try {
            let resp;
            if (modoEdicion && inmuebleActual?.id) {
                resp = await editarInmuebleJWT(idExpediente, inmuebleActual.id, formInmueble);
            } else {
                resp = await crearInmuebleJWT(idExpediente, formInmueble);
            }
            if (resp?.ok) {
                setSnackbar({ open: true, message: modoEdicion ? 'Inmueble actualizado' : 'Inmueble creado', severity: 'success' });
                await cargarInmuebles();
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || 'Error al guardar', severity: 'error' });
            }
        } catch (error) {
            console.error('Error al guardar inmueble:', error);
            setSnackbar({ open: true, message: 'Error al guardar inmueble', severity: 'error' });
        }
        setOpenDialog(false);
    };

    const handleEliminarInmueble = (inmueble) => {
        setEliminarDialog({
            open: true,
            tipo: 'inmueble',
            nombre: inmueble.nomenclatura || 'sin nomenclatura',
            onConfirm: async () => {
                setEliminarDialog(prev => ({ ...prev, open: false }));
                try {
                    const resp = await eliminarInmuebleJWT(idExpediente, inmueble.id);
                    if (resp?.ok) {
                        setSnackbar({ open: true, message: 'Inmueble eliminado', severity: 'success' });
                        await cargarInmuebles();
                    } else {
                        setSnackbar({ open: true, message: resp?.mensaje || 'Error al eliminar', severity: 'error' });
                    }
                } catch (error) {
                    console.error('Error al eliminar inmueble:', error);
                    setSnackbar({ open: true, message: 'Error al eliminar inmueble', severity: 'error' });
                }
            }
        });
    };

    // ============================================
    // MANEJO DE TITULARES
    // ============================================
    const handleExpandirInmueble = async (inmueble) => {
        if (expandedInmueble === inmueble.id) {
            setExpandedInmueble(null);
            setTitulares([]);
        } else {
            setExpandedInmueble(inmueble.id);
            await cargarTitulares(inmueble.id);
        }
    };

    const cargarTitulares = async (idInmueble) => {
        setLoadingTitulares(true);
        try {
            const resp = await expedienteTitularesJWT(idExpediente, idInmueble);
            if (resp?.ok) {
                setTitulares(normalizeKeys(resp.data || []));
            } else {
                setTitulares([]);
            }
        } catch (error) {
            console.error('Error al cargar titulares:', error);
            setTitulares([]);
        } finally {
            setLoadingTitulares(false);
        }
    };

    const handleAbrirDialogTitular = (inmueble, titular = null) => {
        setInmuebleSeleccionado(inmueble);

        if (titular) {
            setModoEdicionTitular(true);
            setTitularActual(titular);
            setFormTitular({
                id_caracter: titular.id_caracter || 0,
                id_tipo_documento: titular.id_tipo_documento || 'A',
                dni: String(titular.id || ''),
                razon_social: titular.razon_social || '',
                nombre: titular.nombre || '',
                direccion: titular.direccion || '',
                cp: titular.cp || 0
            });

            setContraparteSeleccionada({
                id: titular.id,
                nombre: (titular.razon_social || '') + ' ' + (titular.nombre || '')
            });
        } else {
            setModoEdicionTitular(false);
            setTitularActual(null);
            setFormTitular({
                id_caracter: 0,
                id_tipo_documento: 'A',
                dni: '',
                razon_social: '',
                nombre: '',
                direccion: '',
                cp: 0
            });
            setContraparteSeleccionada(null);
        }
        setOpenDialogTitular(true);
    };
        
    const handleGuardarTitular = async () => {
        if (!inmuebleSeleccionado) return;
        try {
            let resp;
            if (modoEdicionTitular && titularActual) {
                const idContraparte = titularActual.id || titularActual.id_contraparte || titularActual.dni;
                resp = await editarTitularJWT(idExpediente, inmuebleSeleccionado.id, idContraparte, formTitular);
            } else {
                resp = await crearTitularJWT(idExpediente, inmuebleSeleccionado.id, formTitular);
            }
            if (resp?.ok) {
                setSnackbar({ open: true, message: modoEdicionTitular ? 'Titular actualizado' : 'Titular agregado', severity: 'success' });
                await cargarTitulares(inmuebleSeleccionado.id);
                await cargarInmuebles();
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || 'Error al guardar', severity: 'error' });
            }
        } catch (error) {
            console.error('Error al guardar titular:', error);
            setSnackbar({ open: true, message: 'Error al guardar titular', severity: 'error' });
        }
        setOpenDialogTitular(false);
    };

    const handleEliminarTitular = (inmueble, titular) => {
        setEliminarDialog({
            open: true,
            tipo: 'titular',
            nombre: titular.persona || titular.razon_social || '',
            onConfirm: async () => {
                setEliminarDialog(prev => ({ ...prev, open: false }));
                try {
                    const resp = await eliminarTitularJWT(idExpediente, inmueble.id, titular.id);
                    if (resp?.ok) {
                        setSnackbar({ open: true, message: 'Titular eliminado', severity: 'success' });
                        await cargarTitulares(inmueble.id);
                        await cargarInmuebles();
                    } else {
                        setSnackbar({ open: true, message: resp?.mensaje || 'Error al eliminar', severity: 'error' });
                    }
                } catch (error) {
                    console.error('Error al eliminar titular:', error);
                    setSnackbar({ open: true, message: 'Error al eliminar titular', severity: 'error' });
                }
            }
        });
    };

    // ============================================
    // RENDER
    // ============================================
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    <HomeIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Inmuebles del Expediente
                </Typography>
                <Tooltip title={perm.insert ? "Agregar inmueble" : "No tenés permiso para agregar"}>
                    <span>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleAbrirDialogInmueble()}
                            style={{ textTransform: 'none', fontWeight: 600 }}
                            disabled={!perm.insert}
                        >
                            Agregar Inmueble
                        </Button>
                    </span>
                </Tooltip>
            </Box>
            {/* Lista de inmuebles */}
            {inmuebles.length === 0 ? (
                <Paper elevation={2} style={{ padding: 40, textAlign: 'center' }}>
                    <HomeIcon style={{ fontSize: 60, color: '#ccc', marginBottom: 16 }} />
                    <Typography variant="body1" color="text.secondary">
                        No hay inmuebles registrados
                    </Typography>
                    <Typography variant="body2" color="text.secondary" style={{ marginTop: 8 }}>
                        Haga clic en "Agregar Inmueble" para comenzar
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    {inmuebles.map((inmueble) => (
                        <Paper
                            key={inmueble.id}
                            elevation={2}
                            style={{
                                marginBottom: 16,
                                borderRadius: 4,
                                overflow: 'hidden'
                            }}
                        >
                            {/* Header del inmueble */}
                            <Box
                                style={{
                                    background: tipo === 'judicial'
                                        ? 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)'
                                        : 'linear-gradient(90deg, #d32f2f 0%, #c62828 100%)',
                                    color: 'white',
                                    padding: 16
                                }}
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, md: 7 }}>
                                        {/* ⭐ ARREGLO: Agregar espacio entre elementos */}
                                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                            <HomeIcon />
                                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                                {inmueble.nomenclatura || 'Sin nomenclatura'}
                                            </Typography>
                                            {inmueble.interes_fiscal === 'SI' && (
                                                <Chip
                                                    icon={<WarningIcon style={{ color: '#ff5722' }} />}
                                                    label="INTERÉS FISCAL"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#ffebee',
                                                        color: '#c62828',
                                                        fontWeight: 700,
                                                        border: '2px solid #ef5350',
                                                        marginLeft: 8 // ⭐ Espacio adicional
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2" style={{ opacity: 0.9, marginTop: 4 }}>
                                            {inmueble.departamento || 'Sin departamento'} •{' '}
                                            {inmueble.ubicacion || 'Sin ubicación'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 5 }}>
                                        {/* ⭐ ARREGLO: Agregar espacio entre botones */}
                                        <Box display="flex" gap={1.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
                                            <Chip
                                                icon={<PersonIcon />}
                                                label={`${inmueble.cantidad_titulares || 0} titular${inmueble.cantidad_titulares !== 1 ? 'es' : ''}`}
                                                size="small"
                                                style={{
                                                    backgroundColor: 'white',
                                                    color: tipo === 'judicial' ? '#1976d2' : '#d32f2f',
                                                    fontWeight: 600
                                                }}
                                            />
                                            <Tooltip title="Ver titulares">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleExpandirInmueble(inmueble)}
                                                    style={{ backgroundColor: 'white', color: tipo === 'judicial' ? '#1976d2' : '#d32f2f' }}
                                                >
                                                    {expandedInmueble === inmueble.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={perm.update ? "Editar" : "No tenés permiso para editar"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleAbrirDialogInmueble(inmueble)}
                                                        style={{ backgroundColor: 'white', color: tipo === 'judicial' ? '#1976d2' : '#d32f2f' }}
                                                        disabled={!perm.update}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={perm.delete ? "Eliminar" : "No tenés permiso para eliminar"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEliminarInmueble(inmueble)}
                                                        style={{ backgroundColor: 'white', color: '#d32f2f' }}
                                                        disabled={!perm.delete}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Detalles del inmueble */}
                            <Box p={2}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                        <Typography variant="caption" color="text.secondary" style={{ fontWeight: 600 }}>
                                            Folio Real
                                        </Typography>
                                        <Typography variant="body2">
                                            {inmueble.real || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                        <Typography variant="caption" color="text.secondary" style={{ fontWeight: 600 }}>
                                            Tomo
                                        </Typography>
                                        <Typography variant="body2">
                                            {inmueble.tomo || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                        <Typography variant="caption" color="text.secondary" style={{ fontWeight: 600 }}>
                                            Folio
                                        </Typography>
                                        <Typography variant="body2">
                                            {inmueble.folio || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                        <Typography variant="caption" color="text.secondary" style={{ fontWeight: 600 }}>
                                            Inscripción
                                        </Typography>
                                        <Typography variant="body2">
                                            {inmueble.inscripcion || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                        <Typography variant="caption" color="text.secondary" style={{ fontWeight: 600 }}>
                                            Sup. Título
                                        </Typography>
                                        <Typography variant="body2">
                                            {inmueble.sup_titulo || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                        <Typography variant="caption" color="text.secondary" style={{ fontWeight: 600 }}>
                                            Nº Plano
                                        </Typography>
                                        <Typography variant="body2">
                                            {inmueble.plano || '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Titulares (expandible) */}
                            <Collapse in={expandedInmueble === inmueble.id}>
                                <Box p={2} bgcolor="#fafafa" borderTop="1px solid #e0e0e0">
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                            <PersonIcon style={{ verticalAlign: 'middle', marginRight: 4, fontSize: 18 }} />
                                            Titulares
                                        </Typography>
                                        <Tooltip title={perm.insert ? "Agregar titular" : "No tenés permiso para agregar"}>
                                            <span>
                                                <Button
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleAbrirDialogTitular(inmueble)}
                                                    variant="contained"
                                                    color="primary"
                                                    style={{ textTransform: 'none', fontWeight: 600 }}
                                                    disabled={!perm.insert}
                                                >
                                                    Agregar Titular
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Box>

                                    {loadingTitulares ? (
                                        <Box display="flex" justifyContent="center" py={2}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : titulares.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic', textAlign: 'center', padding: 16 }}>
                                            No hay titulares registrados
                                        </Typography>
                                    ) : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ fontWeight: 700 }}>Carácter</TableCell>
                                                    <TableCell style={{ fontWeight: 700 }}>Persona</TableCell>
                                                    <TableCell style={{ fontWeight: 700 }}>DNI/CUIT</TableCell>
                                                    <TableCell style={{ fontWeight: 700 }}>Dirección</TableCell>
                                                    <TableCell align="center" style={{ fontWeight: 700 }}>Acciones</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {titulares.map((titular, idx) => (
                                                    <TableRow key={titular.id} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                        <TableCell>{titular.caracter}</TableCell>
                                                        <TableCell>{titular.persona}</TableCell>
                                                        <TableCell>{titular.dni}</TableCell>
                                                        <TableCell>{titular.direccion || '-'}</TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title={perm.update ? "Editar" : "No tenés permiso para editar"}>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleAbrirDialogTitular(inmueble, titular)}
                                                                        disabled={!perm.update}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title={perm.delete ? "Eliminar" : "No tenés permiso para eliminar"}>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleEliminarTitular(inmueble, titular)}
                                                                        color="secondary"
                                                                        disabled={!perm.delete}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Box>
                            </Collapse>
                        </Paper>
                    ))}
                </Box>
            )}
            {/* ============================================ */}
            {/* DIALOG DE INMUEBLE */}
            {/* ============================================ */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                    <Box display="flex" alignItems="center">
                        <HomeIcon style={{ marginRight: 8, color: tipo === 'judicial' ? '#1976d2' : '#d32f2f' }} />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {modoEdicion ? 'Editar Inmueble' : 'Agregar Inmueble'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent style={{ paddingTop: 16 }}>
                    {/* Tabs */}
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => setTabValue(v)}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}
                    >
                        <Tab label="Datos Catastrales" />
                        <Tab label="Ubicación" />
                        <Tab label="Observaciones" />
                        <Tab label="Demanda / Utilidad Pública" />
                    </Tabs>

                    {/* TAB 0: Datos Catastrales */}
                    {tabValue === 0 && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Nomenclatura Catastral"
                                    placeholder="99-99-999-999-99-999"
                                    value={formInmueble.nomenclatura}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, nomenclatura: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Folio Real"
                                    placeholder="99-99999"
                                    value={formInmueble.real}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, real: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Tomo"
                                    value={formInmueble.tomo}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, tomo: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Folio"
                                    value={formInmueble.folio}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, folio: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Nº Inscripción"
                                    value={formInmueble.inscripcion}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, inscripcion: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Año"
                                    value={formInmueble.ano}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, ano: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Superficie Título"
                                    value={formInmueble.sup_titulo}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, sup_titulo: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel>Departamento</InputLabel>
                                    <Select
                                        value={formInmueble.dpto}
                                        onChange={(e) => setFormInmueble({ ...formInmueble, dpto: e.target.value })}
                                        label="Departamento"
                                    >
                                        <MenuItem value={0}>-- Seleccionar --</MenuItem>
                                        {departamentos.map(d => (
                                            <MenuItem key={d.id} value={d.id}>{d.nombre}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Nº de Plano"
                                    placeholder="99/99999-9999"
                                    value={formInmueble.plano}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, plano: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Superficie Plano"
                                    value={formInmueble.sup_plano}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, sup_plano: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* TAB 1: Ubicación */}
                    {tabValue === 1 && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Ubicación"
                                    value={formInmueble.ubicacion}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, ubicacion: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel>Localidad</InputLabel>
                                    <Select
                                        value={formInmueble.cp}
                                        onChange={(e) => setFormInmueble({ ...formInmueble, cp: e.target.value })}
                                        label="Localidad"
                                    >
                                        <MenuItem value="">-- Seleccionar --</MenuItem>
                                        {localidades.map(l => (
                                            <MenuItem key={l.id} value={l.id}>{l.nombre}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel>Interés Fiscal</InputLabel>
                                    <Select
                                        value={formInmueble.interes_fiscal}
                                        onChange={(e) => setFormInmueble({ ...formInmueble, interes_fiscal: e.target.value })}
                                        label="Interés Fiscal"
                                    >
                                        <MenuItem value="SI">SI</MenuItem>
                                        <MenuItem value="NO">NO</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel>Inspección F.E.</InputLabel>
                                    <Select
                                        value={formInmueble.inspeccion_fe}
                                        onChange={(e) => setFormInmueble({ ...formInmueble, inspeccion_fe: e.target.value })}
                                        label="Inspección F.E."
                                    >
                                        <MenuItem value="SI">SI</MenuItem>
                                        <MenuItem value="NO">NO</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {/* TAB 2: Observaciones */}
                    {tabValue === 2 && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Antecedentes Catastrales"
                                    value={formInmueble.obs_antecedentes}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_antecedentes: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Mejoras"
                                    value={formInmueble.obs_mejoras}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_mejoras: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Cauces de agua existentes o colindantes"
                                    value={formInmueble.obs_cauces_agua}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_cauces_agua: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Deslinde del polígono o parcela relevada"
                                    value={formInmueble.obs_deslinde}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_deslinde: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Servidumbres"
                                    value={formInmueble.obs_servidumbres}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_servidumbres: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Fijación de línea municipal o zona de camino"
                                    value={formInmueble.obs_fijacion}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_fijacion: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Georeferenciación"
                                    value={formInmueble.obs_geo}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_geo: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Observaciones Generales"
                                    value={formInmueble.obs_observaciones}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, obs_observaciones: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* TAB 3: Demanda / Utilidad Pública */}
                    {tabValue === 3 && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Monto s/ Demanda"
                                    value={formInmueble.demanda_monto}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, demanda_monto: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Fecha"
                                    type="date"
                                    value={formInmueble.demanda_fecha}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, demanda_fecha: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Observaciones Demanda"
                                    value={formInmueble.demanda_obs}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, demanda_obs: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Utilidad Pública - Ley Nº"
                                    value={formInmueble.ley}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, ley: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Monto Depositado"
                                    value={formInmueble.monto_depositado}
                                    onChange={(e) => setFormInmueble({ ...formInmueble, monto_depositado: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                    <Button
                        onClick={() => setOpenDialog(false)}
                        color="secondary"
                        style={{ textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleGuardarInmueble}
                        color="primary"
                        variant="contained"
                        style={{
                            minWidth: 120,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* ============================================ */}
            {/* DIALOG DE TITULAR */}
            {/* ============================================ */}
            <Dialog
                open={openDialogTitular}
                onClose={() => setOpenDialogTitular(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                    <Box display="flex" alignItems="center">
                        <PersonIcon style={{ marginRight: 8, color: tipo === 'judicial' ? '#1976d2' : '#d32f2f' }} />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {modoEdicionTitular ? 'Editar Titular' : 'Agregar Titular'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent style={{ paddingTop: 24 }}>
                    <Grid container spacing={2}>
                        {/* Carácter */}
                        <Grid size={12}>
                            <FormControl fullWidth variant="outlined" size="small" required>
                                <InputLabel>Carácter</InputLabel>
                                <Select
                                    value={formTitular.id_caracter}
                                    onChange={(e) => setFormTitular({ ...formTitular, id_caracter: e.target.value })}
                                    label="Carácter"
                                >
                                    <MenuItem value={0}>-- Seleccionar --</MenuItem>
                                    {caracteres.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Tipo de documento */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth variant="outlined" size="small" required>
                                <InputLabel>Tipo de documento</InputLabel>
                                <Select
                                    value={formTitular.id_tipo_documento}
                                    onChange={(e) => handleTipoDocumentoChange(e.target.value)}
                                    label="Tipo de documento"
                                >
                                    {(Array.isArray(tiposDocumento) && tiposDocumento.length > 0
                                        ? tiposDocumento
                                        : [{ id: 'A', nombre: 'DNI' }, { id: 'C', nombre: 'CUIT' }]
                                    ).map(td => (
                                        <MenuItem key={td.id} value={td.id}>
                                            {td.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* DNI / CUIT manual */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="DNI / CUIT"
                                value={formTitular.dni}
                                onChange={(e) => setFormTitular({ ...formTitular, dni: e.target.value.replace(/\D/g, '') })}
                                onBlur={handleDniBlur}
                                variant="outlined"
                                size="small"
                                required
                                disabled={modoEdicionTitular}
                                placeholder="Ingrese DNI/CUIT y presione Tab para buscar"
                                helperText={modoEdicionTitular ? "No se puede cambiar el DNI" : "Al salir del campo se busca automáticamente"}
                            />
                        </Grid>

                        {/* Autocomplete de Contraparte (solo en modo crear) */}
                        {!modoEdicionTitular && (
                        <Grid size={12}>
                            <Autocomplete
                                options={contrapartes}
                                getOptionLabel={(opt) => {
                                    const id = String(opt.id || '');
                                    const nombre = opt.nombre || '';
                                    return id ? `${nombre} (DNI/CUIT: ${id})` : nombre;
                                }}
                                filterOptions={(x) => x}
                                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                loading={loadingContrapartes}
                                value={contraparteSeleccionada}
                                onInputChange={(e, value, reason) => {
                                    if (reason === 'input' && value.length >= 2) {
                                        fetchContrapartes(value);
                                    }
                                }}
                                onChange={(e, val) => {
                                    if (val) {
                                        setFormTitular({
                                            ...formTitular,
                                            dni: String(val.id || ''),
                                            id_tipo_documento: val.id_tipo_documento || formTitular.id_tipo_documento,
                                            razon_social: val.razon_social || '',
                                            nombre: val.nombre_persona || '',
                                            direccion: val.direccion || '',
                                            cp: val.cp || 0
                                        });
                                        setContraparteSeleccionada(val);
                                    } else {
                                        setFormTitular({
                                            ...formTitular,
                                            dni: '',
                                            razon_social: '',
                                            nombre: '',
                                            direccion: '',
                                            cp: 0
                                        });
                                        setContraparteSeleccionada(null);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Buscar por DNI/CUIT o Razón Social/Apellido"
                                        placeholder="Escriba al menos 2 caracteres..."
                                        variant="outlined"
                                        size="small"
                                        required
                                        helperText="La búsqueda es en tiempo real mientras escribe"
                                    />
                                )}
                            />
                        </Grid>
                        )}

                        {/* Razón Social / Apellido — label dinámico según tipo doc.
                            'A' = DNI (física): "Apellido" + campo Nombre visible
                            'C' = CUIT (jurídica): "Razón Social" + campo Nombre oculto */}
                        <Grid size={{ xs: 12, sm: formTitular.id_tipo_documento === 'C' ? 12 : 6 }}>
                            <TextField
                                fullWidth
                                label={formTitular.id_tipo_documento === 'C' ? 'Razón Social' : 'Apellido'}
                                value={formTitular.razon_social}
                                onChange={(e) => setFormTitular({ ...formTitular, razon_social: e.target.value.toUpperCase() })}
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength: 100 }}
                            />
                        </Grid>
                        {formTitular.id_tipo_documento !== 'C' && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Nombre"
                                    value={formTitular.nombre}
                                    onChange={(e) => setFormTitular({ ...formTitular, nombre: e.target.value.toUpperCase() })}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ maxLength: 50 }}
                                />
                            </Grid>
                        )}

                        {/* Dirección */}
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <TextField
                                fullWidth
                                label="Dirección"
                                value={formTitular.direccion}
                                onChange={(e) => setFormTitular({ ...formTitular, direccion: e.target.value.toUpperCase() })}
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength: 200 }}
                            />
                        </Grid>

                        {/* Localidad */}
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Localidad</InputLabel>
                                <Select
                                    value={formTitular.cp}
                                    onChange={(e) => setFormTitular({ ...formTitular, cp: e.target.value })}
                                    label="Localidad"
                                >
                                    <MenuItem value={0}>-- Seleccionar --</MenuItem>
                                    {localidades.map(l => (
                                        <MenuItem key={l.id} value={l.id}>{l.nombre}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                    <Button
                        onClick={() => setOpenDialogTitular(false)}
                        color="secondary"
                        style={{ textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleGuardarTitular}
                        color="primary"
                        variant="contained"
                        style={{
                            minWidth: 120,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Dialog de confirmación eliminar */}
            <Dialog
                open={eliminarDialog.open}
                onClose={() => setEliminarDialog(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
                    <WarningIcon /> Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que querés eliminar {eliminarDialog.tipo === 'titular' ? 'el titular' : 'el inmueble'}{' '}
                        <strong>{eliminarDialog.nombre}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEliminarDialog(prev => ({ ...prev, open: false }))} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={eliminarDialog.onConfirm} variant="contained" color="error">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ⭐ Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default InmueblesTab;